import { html, css } from 'lit';
import { StyleInfo, styleMap } from 'lit-html/directives/style-map';
import { customElement, property, query, state } from 'lit/decorators.js';
import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import { TRANSITION_DURATION_FAST } from '../common/transition';
import { waitMs } from '../common/utils';
import { TeTableCell } from './te-table-cell';
import { TeTableColumn } from './te-table-column';
import { TeTableRow } from './te-table-row';

/**
 * Table Component
 *
 * @slot - Default slot for table content (rows)
 * @slot header - Slot for rendering a sticky table header
 * @slot column - Slot for rendering a sticky table column
 * @slot corner - Slot for rendering corner piece between sticky header and column
 */
@customElement('te-table')
export class TeTable extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-table {
        position: relative;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        flex: 1;
        max-height: 100%;
        max-width: 100%;
        background-color: ${theme.colors.shade0};
        border: 1px solid ${theme.colors.shade2};
        border-radius: 4px;
      }

      .te-table-scrollable {
        overflow: auto;
        flex: 1;
      }

      .sticky-header-corner {
        display: flex;
        border-bottom: 1px solid ${theme.colors.shade2};
        border-right: 1px solid ${theme.colors.shade2};
        z-index: 1;
      }

      .te-table-sticky-header {
        display: flex;
        flex-direction: row;
        flex: 1;
        z-index: 1;
      }

      .te-table-content {
        display: flex;
        flex: 1;
        overflow: hidden;
      }

      .sticky-column-content {
        width: fit-content;
        z-index: 1;
        overflow: hidden;
      }

      .sticky-header-content {
        flex: 1;
        overflow: hidden;
      }

      .te-table-footer {
        box-shadow: ${theme.shadows.top};
        min-height: 16px;
        flex: 1 0;
        z-index: 1;
      }

      .te-table-gradient {
        pointer-events: none;
        position: absolute;
        border-radius: 0 4px 4px 0;
        right: 0;
        top: 0;
        width: 64px;
        z-index: 2;
        height: 100%;
        transition-property: width;
        transition-duration: ${TRANSITION_DURATION_FAST}ms;
        background: linear-gradient(
          90deg,
          ${theme.colors.shade0}00 0%,
          ${theme.colors.shade0}AA 38%,
          ${theme.colors.shade0} 97%,
          ${theme.colors.shade0} 100%
        );
      }

      ::slotted(corner) {
        border-right: 1px solid ${theme.colors.shade2};
        cursor: pointer;
      }

      slot[name='corner']::slotted(*) {
        cursor: pointer;
      }

      slot[name='header']::slotted(*) {
        cursor: pointer;
      }
    `,
  ];

  /** Minimum table cell size */
  @property({ type: Number }) minimumCellSize = 48;

  @state() columns: number[] = [];
  @state() rows: number[] = [];

  @state() hasStaticHeader = true;
  @state() hasStaticColumn = true;
  @state() hasStaticCorner = true;
  @state() hasPlaceholderCorner = false;

  @state() private scrollX = false;
  @state() private scrollY = false;
  @state() private scrollGradient = true;
  @state() private resizeCallback: () => void = () => {
    return;
  };

  @query('.te-sticky-column') stickyColumn?: HTMLSlotElement;
  @query('.te-sticky-corner') stickyCorner?: HTMLSlotElement;
  @query('.te-sticky-header') stickyHeader?: HTMLSlotElement;
  @query('.sticky-header-content') stickyHeaderContent?: HTMLDivElement;
  @query('.sticky-column-content') stickyColumnContent?: HTMLDivElement;
  @query('.te-table-scrollable') scrollableArea?: HTMLDivElement;
  @query('.te-forceful-corner-cell') forcedCornerCell?: TeTableCell;

  isCell(elem: Element): elem is TeTableCell {
    return elem.nodeName === 'TE-TABLE-CELL';
  }
  isRow(elem: Element): elem is TeTableRow {
    return elem.nodeName === 'TE-TABLE-ROW';
  }
  isColumn(elem: Element): elem is TeTableColumn {
    return elem.nodeName === 'TE-TABLE-COLUMN';
  }

  firstUpdated() {
    if (!this.stickyCorner || !this.stickyColumn || !this.stickyHeader) return;

    this.hasStaticColumn = this.stickyColumn.assignedNodes({ flatten: true }).length > 0;
    this.hasStaticHeader = this.stickyHeader.assignedNodes({ flatten: true }).length > 0;
    this.hasStaticCorner = this.stickyCorner.assignedNodes({ flatten: true }).length > 0;

    if (!this.hasStaticCorner && this.hasStaticColumn && this.hasStaticHeader) {
      // Forcefully create a empty cell to corner if it is missing
      this.hasStaticCorner = true;
      const cell = new TeTableCell();
      cell.classList.add('te-forceful-corner-cell');
      cell.placeholderCellParent = this;
      this.stickyCorner.appendChild(cell);
    }

    this.resizeCallback = this.handleResize.bind(this);
    window.addEventListener('resize', this.resizeCallback);
    // Allow for some time to render before updating sizes
    waitMs(10).then(() => {
      this.handleResize();
      this.resizeAllCells();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resizeCallback);
  }

  updateScrollbarOffsets(div: HTMLDivElement) {
    const scrollbarWidth = div.offsetWidth - div.clientWidth;
    const scrollbarHeight = div.offsetHeight - div.clientHeight;

    if (this.stickyHeader) {
      const stickyHeader = this.stickyHeader
        .assignedNodes({ flatten: true })
        .find((node) => node.nodeName === 'TE-TABLE-ROW');
      if (stickyHeader) {
        (stickyHeader as TeTableRow).scrollbarOffset = scrollbarWidth;
      }
    }

    if (this.stickyColumn) {
      const stickyColumn = this.stickyColumn
        .assignedNodes({ flatten: true })
        .find((node) => node.nodeName === 'TE-TABLE-COLUMN');
      if (stickyColumn) {
        (stickyColumn as TeTableColumn).scrollbarOffset = scrollbarHeight;
      }
    }
  }

  updateScrollShadows(div: HTMLDivElement) {
    this.scrollX = div.scrollWidth > Math.max(div.clientWidth, div.offsetWidth);
    this.scrollY = div.scrollHeight > Math.max(div.clientHeight, div.offsetHeight);
    this.scrollGradient = this.scrollX;
  }

  handleResize() {
    if (!this.scrollableArea) return;
    this.updateScrollShadows(this.scrollableArea);
    this.updateScrollbarOffsets(this.scrollableArea);
  }

  setColumn(index: number, value: number) {
    if (!this.columns[index] || this.columns[index] < value) {
      this.columns[index] = value;
    }
  }

  getColumn(index: number) {
    return this.columns[index] || 0;
  }

  setRow(index: number, value: number) {
    const newValue = Math.max(value, this.minimumCellSize);
    if (!this.rows[index] || this.rows[index] < newValue) {
      this.rows[index] = newValue;
    }
  }

  getRow(index: number) {
    if (!this.rows[index]) return 0;
    return this.rows[index];
  }

  getCells() {
    const children = [...this.children];
    const cells = children.filter(this.isCell);
    const rows = children.filter(this.isRow);
    const columns = children.filter(this.isColumn);
    const rowCells = rows.flatMap((row) => [...row.children].filter(this.isCell));
    const columnCells = columns.flatMap((row) => [...row.children].filter(this.isCell));
    const allCells = [...cells, ...rowCells, ...columnCells];
    this.forcedCornerCell && allCells.push(this.forcedCornerCell);
    return allCells;
  }

  async resizeAllCells() {
    // Reset cells to minimum dimensions
    this.rows = this.rows.map(() => this.minimumCellSize);
    this.columns = this.columns.map(() => this.minimumCellSize);

    const cells = this.getCells();

    cells.forEach((cell) => cell.updateSize());

    await waitMs(10);

    cells.forEach((cell) => cell.resizeCell());
  }

  handleScroll(e: MouseEvent) {
    if (!e.currentTarget) return;
    const target = e.currentTarget as HTMLDivElement;

    if (this.stickyColumnContent) {
      this.stickyColumnContent.scrollTop = target.scrollTop;
    }

    if (this.stickyHeaderContent) {
      this.stickyHeaderContent.scrollLeft = target.scrollLeft;
      // Hide gradient when scrolled to the right
      this.scrollGradient = target.scrollWidth > target.scrollLeft + target.clientWidth;
    }
  }

  getHeaderStyle(): StyleInfo {
    if (!this.hasStaticHeader) return { display: 'none' };
    else if (this.scrollY) return { boxShadow: theme.shadows.right.toString() };
    return {};
  }

  getCornerStyle(): StyleInfo {
    if (!this.hasStaticCorner && !this.hasPlaceholderCorner) return { display: 'none' };
    else if (this.scrollX) return { boxShadow: theme.shadows.right.toString() };
    return {};
  }

  getColumnStyle(): StyleInfo {
    if (!this.hasStaticColumn) return { display: 'none' };
    else if (this.scrollX) return { boxShadow: theme.shadows.right.toString() };
    return {};
  }

  renderFooter() {
    if (!this.scrollY) return;
    return html`
      <div class="te-table-footer">
        <slot name="footer"></slot>
      </div>
    `;
  }

  getGradientStyle(): StyleInfo {
    return this.scrollGradient ? {} : { width: '0px' };
  }

  setRowHover(row: number, hover: boolean) {
    if (row === 0 && (this.hasStaticCorner || this.hasStaticHeader)) return;
    const cells = this.getCells();
    const cellsInRow = cells.filter((cell) => cell.rowIndex === row);
    cellsInRow.forEach((cell) => {
      if (cell.hover !== hover) cell.hover = hover;
    });
  }

  setRowSelected(row: number, selected: boolean) {
    const cells = this.getCells();
    const cellsInRow = cells.filter((cell) => cell.rowIndex === row);
    cellsInRow.forEach((cell) => {
      if (cell.selected !== selected) cell.selected = selected;
    });
  }

  render() {
    return html`
      <div class="te-table">
        <div class="te-table-gradient" style=${styleMap(this.getGradientStyle())}></div>
        <div class="te-table-sticky-header" style=${styleMap(this.getHeaderStyle())}>
          <div class="sticky-header-corner" style=${styleMap(this.getCornerStyle())}>
            <slot name="corner" class="te-sticky-corner"></slot>
          </div>
          <div class="sticky-header-content">
            <slot name="header" class="te-sticky-header"></slot>
          </div>
        </div>

        <div class="te-table-content">
          <div class="sticky-column-content" style=${styleMap(this.getColumnStyle())}>
            <slot name="column" class="te-sticky-column"></slot>
          </div>
          <div class="te-table-scrollable" @scroll=${this.handleScroll}>
            <slot class="te-table-slot"></slot>
          </div>
        </div>

        ${this.renderFooter()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-table': TeTable;
  }
}
